import React, { useState } from 'react';
import { Box, Typography, Tooltip, Skeleton } from '@mui/material';
import useVisiblePoll from '../hooks/useVisiblePoll';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import StreamMonitor from './StreamMonitor';
import { getOverview } from '../services/api';

import { fmtCount as fmt, ago, until } from '../utils/time';

/* ── stat card (factual counts only — no threat judgments) ─── */
function StatCard({ icon, value, label, sub, accent }) {
  return (
    <Box sx={{
      flex: '1 1 150px', minWidth: 140, p: 1.8, borderRadius: 2.5, position: 'relative',
      overflow: 'hidden',
      background: `linear-gradient(150deg, ${accent}12 0%, rgba(19,16,31,0.4) 55%)`,
      border: '1px solid rgba(255,255,255,0.07)',
      transition: 'transform .18s ease, border-color .18s ease',
      '&:hover': { transform: 'translateY(-2px)', borderColor: `${accent}55` },
    }}>
      <Box sx={{ position: 'absolute', top: -10, right: -10, opacity: 0.09, color: accent }}>
        {React.cloneElement(icon, { sx: { fontSize: 72 } })}
      </Box>
      <Box display="flex" alignItems="center" gap={0.8} sx={{ color: accent, mb: 0.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
        <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, letterSpacing: 0.6,
                          textTransform: 'uppercase', color: 'rgba(232,228,248,0.5)' }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: '#f2eefc',
                        letterSpacing: -1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.66rem', color: 'rgba(232,228,248,0.4)', mt: 0.6 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/* ── live component status tile ──────────────────────────── */
function StatusTile({ label, on, color, value, detail, pulse }) {
  const c = on ? (color || '#4ade80') : 'rgba(232,228,248,0.3)';
  return (
    <Tooltip title={detail || ''} placement="top" arrow
      slotProps={{ tooltip: { sx: { bgcolor: '#1a1430', border: '1px solid rgba(255,255,255,0.12)', fontSize: '0.72rem' } }, arrow: { sx: { color: '#1a1430' } } }}>
      <Box sx={{ flex: '1 1 120px', minWidth: 110, px: 1.4, py: 1.1, borderRadius: 2,
                 bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Box display="flex" alignItems="center" gap={0.8}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%', bgcolor: c, flexShrink: 0,
            boxShadow: on ? `0 0 8px ${c}` : 'none',
            ...(pulse && on ? {
              '@keyframes livepulse': { '0%,100%': { opacity: 1, transform: 'scale(1)' }, '50%': { opacity: 0.35, transform: 'scale(0.8)' } },
              animation: 'livepulse 1.4s ease-in-out infinite',
            } : {}),
          }} />
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.4,
                            textTransform: 'uppercase', color: 'rgba(232,228,248,0.55)' }}>
            {label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#e8e4f8', mt: 0.4 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/* ── factual breakdown chips ─────────────────────────────── */
function BreakdownRow({ title, items }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.6,
                        textTransform: 'uppercase', color: 'rgba(232,228,248,0.3)', mb: 1 }}>
        {title}
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={1}>
        {items.map(([key, label, color, count]) => (
          <Box key={key} display="flex" alignItems="center" gap={0.7}
            sx={{ px: 1.1, py: 0.6, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.6)' }}>
              {label} <b style={{ color: '#e8e4f8' }}>{count ?? 0}</b>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const Panel = ({ children, sx }) => (
  <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#13101f',
             border: '1px solid rgba(255,255,255,0.07)', ...sx }}>{children}</Box>
);
const Heading = ({ children }) => (
  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.8,
                    textTransform: 'uppercase', color: 'rgba(232,228,248,0.35)', mb: 1.5 }}>
    {children}
  </Typography>
);

/* ── main ────────────────────────────────────────────────── */
function Overview({ refreshTrigger }) {
  const [data, setData] = useState(null);

  useVisiblePoll(() => getOverview()
    .then(({ data }) => setData(data))
    .catch(() => {}), 4000, [refreshTrigger]);

  if (!data) {
    return (
      <Box display="flex" gap={1.5} mb={2.5} flexWrap="wrap">
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={92} sx={{ flex: '1 1 150px', bgcolor: 'rgba(255,255,255,0.04)' }} />
        ))}
      </Box>
    );
  }

  const { headline: h, stream, nrd, scans, feeds, by_classification, by_source, by_risk_band, by_brand, features, crtsh, recent_stream, precision_by_channel, ct_loglist } = data;
  const precision = precision_by_channel || [];
  const drift = ct_loglist || null;
  const phishtank = (feeds || []).find(f => f.name === 'phishtank');
  const cls = by_classification || {};
  const rb = by_risk_band || {};
  const brands = by_brand || [];
  const crtshKnown = crtsh && crtsh.healthy !== null && crtsh.healthy !== undefined;

  return (
    <Box mb={2.5}>
      {/* headline counts — general statistics, descriptive only */}
      <Box display="flex" gap={1.5} flexWrap="wrap" mb={1.5}>
        <StatCard icon={<TravelExploreOutlinedIcon />} accent="#a78bfa"
          value={fmt(h.total)} label="Detected domains" sub="across all sources" />
        <StatCard icon={<TodayOutlinedIcon />} accent="#4ade80"
          value={fmt(h.new_today)} label="New (24h)" sub="detected in last day" />
        <StatCard icon={<DateRangeOutlinedIcon />} accent="#2dd4bf"
          value={fmt(h.new_7d)} label="New (7 days)" sub="detected this week" />
        <StatCard icon={<DomainOutlinedIcon />} accent="#60a5fa"
          value={fmt(h.brands)} label="Monitored brands" sub="being watched" />
      </Box>

      {/* stretch: both panels share the row height (set by the taller
          Detections panel); the Live Monitor's stream-catches list flexes
          to fill the difference so there is no dead space below either. */}
      <Box display="flex" gap={1.5} flexWrap="wrap" sx={{ '& > *': { flex: '1 1 320px' } }}>
        {/* LIVE MONITOR */}
        <Panel sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Heading>Live Monitoring</Heading>
            <Box display="flex" alignItems="center" gap={0.7}
              sx={{ px: 1, py: 0.3, borderRadius: 5,
                    bgcolor: stream.connected ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${stream.connected ? 'rgba(74,222,128,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%',
                bgcolor: stream.connected ? '#4ade80' : 'rgba(232,228,248,0.3)',
                boxShadow: stream.connected ? '0 0 8px #4ade80' : 'none',
                '@keyframes lp': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
                animation: stream.connected ? 'lp 1.4s ease-in-out infinite' : 'none',
              }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.8,
                                color: stream.connected ? '#4ade80' : 'rgba(232,228,248,0.4)' }}>
                {stream.connected ? 'LIVE' : 'OFFLINE'}
              </Typography>
            </Box>
          </Box>

          {/* Real-time CT stream throughput */}
          <Box mb={1.5}>
            <StreamMonitor stream={stream} loglist={drift} />
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap">
            <StatusTile label="CT stream" on={stream.connected} color="#4ade80" pulse
              value={stream.connected ? 'streaming' : (stream.enabled ? 'reconnecting' : 'off')}
              detail={'Real-time Let\'s Encrypt certificate monitoring (Willow logs).'
                + (drift && drift.status === 'warn'
                  ? ` Log coverage gap: ${(drift.dead || []).length} dead log(s)`
                    + ((drift.new_operators || []).length
                      ? `, operators in service but not followed (${drift.new_operators.join(', ')}); certificates logged only there are invisible to the stream until the config is refreshed.`
                      : '; refresh the stream config.')
                  : '')} />
            <StatusTile label="NRD watch" on={nrd.enabled} color="#2dd4bf"
              value={ago(nrd.last_run)}
              detail={`Daily newly-registered-domains sweep. Last run added ${nrd.last_new ?? 0} new.`} />
            <StatusTile label="Scans" on={scans.enabled} color="#a78bfa"
              value={scans.running ? 'running' : (until(scans.next_run) || ago(scans.last_run))}
              detail={`Scheduled deep scan (all CAs via crt.sh + permutations + re-check). Last: ${ago(scans.last_run)}${scans.partial ? ' (partial)' : ''}.`} />
            <StatusTile label="PhishTank" on={features.phishtank} color="#60a5fa"
              value={phishtank ? `${fmt(phishtank.count)}` : 'off'}
              detail={phishtank ? `Cached ${phishtank.count} phishing domains, refreshed ${ago(phishtank.fetched_at)}` : 'Feed disabled'} />
            <StatusTile label="crt.sh" on={crtshKnown && crtsh.healthy}
              color={crtshKnown && !crtsh.healthy ? '#f87171' : '#4ade80'}
              value={!crtshKnown ? 'not probed' : (crtsh.healthy ? 'reachable' : 'down')}
              detail={!crtshKnown
                ? 'Probed at the start of each deep scan; no probe recorded yet.'
                : `Fallback CT source for deep scans, probed ${ago(crtsh.checked_at)}. ` +
                  (crtsh.healthy
                    ? 'Answering normally.'
                    : 'Currently unreachable: scans mark partial and the live CT stream carries real-time coverage.')} />
          </Box>

          {/* Latest real-time catches from the CT stream. When the panels sit
              side by side (viewport >= 1000px: 300px sidebar + two 320px
              panels), flex-basis 0 keeps this section from inflating the
              panel's natural height: it only EXPANDS into space the
              Detections panel already created, scrolling internally if more
              catches arrive than fit. When the panels STACK (portrait /
              narrow), it takes its natural full height instead: a fixed
              window with an inner scrollbar is a scroll trap on touch. */}
          {(recent_stream || []).length > 0 && (
            <Box mt={1.8} sx={{ display: 'flex', flexDirection: 'column',
                                flex: '0 0 auto',
                                '@media (min-width: 1000px)': { flex: '1 1 0', minHeight: 150 } }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.6,
                                textTransform: 'uppercase', color: 'rgba(232,228,248,0.3)', mb: 0.7 }}>
                Latest stream catches
              </Typography>
              <Box sx={{ '@media (min-width: 1000px)': {
                flex: 1, minHeight: 0, overflowY: 'auto',
                WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
              } }}>
              {recent_stream.map(r => (
                <Box key={r.id} display="flex" alignItems="center" gap={1}
                  sx={{ py: 0.4, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <Typography sx={{
                    fontSize: '0.72rem', fontWeight: 800, width: 24, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                    color: r.risk >= 90 ? '#f87171' : r.risk >= 70 ? '#fb923c'
                      : r.risk >= 40 ? '#a78bfa' : '#4ade80',
                  }}>
                    {r.risk}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.74rem', fontFamily: 'monospace', color: '#e8e4f8',
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {r.domain}
                  </Typography>
                  {r.label && (
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.35)', flexShrink: 0 }}>
                      {r.label}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.6rem', color: 'rgba(232,228,248,0.25)',
                                    flexShrink: 0, minWidth: 48, textAlign: 'right' }}>
                    {ago(r.detected_at)}
                  </Typography>
                </Box>
              ))}
              </Box>
            </Box>
          )}
        </Panel>

        {/* DETECTIONS — general statistical breakdown */}
        <Panel>
          <Heading>Detections</Heading>
          <BreakdownRow title="By detection source" items={[
            ['scan', 'Scans', '#a78bfa', by_source.scan],
            ['nrd', 'NRD', '#2dd4bf', by_source.nrd],
            ['stream', 'CT stream', '#4ade80', by_source.stream],
            ['feed', 'Feeds', '#fbbf24', by_source.feed],
            ['hunt', 'urlscan hunt', '#60a5fa', by_source.hunt],
          ].filter(i => i[3])} />
          <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1.8 }} />
          <BreakdownRow title="By risk band" items={[
            ['critical', 'Critical (90+)', '#f87171', rb.critical],
            ['high', 'High (70–89)', '#fb923c', rb.high],
            ['medium', 'Medium (40–69)', '#a78bfa', rb.medium],
            ['low', 'Low (<40)', '#4ade80', rb.low],
          ].filter(i => i[3])} />
          <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1.8 }} />
          <BreakdownRow title="By classification" items={[
            ['active', 'Active', '#4ade80', cls.active],
            ['redirect', 'Redirect', '#60a5fa', cls.redirect],
            ['parked', 'Parked', '#6b7280', cls.parked],
            ['for_sale', 'For sale', '#fbbf24', cls.for_sale],
            ['inactive', 'Inactive', '#6b7280', cls.inactive],
            ['unclassified', 'Unclassified', 'rgba(232,228,248,0.4)', cls.unclassified],
          ].filter(i => i[3])} />
          {brands.length > 0 && (
            <>
              <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1.8 }} />
              <BreakdownRow title="By brand" items={
                brands.map(b => [b.brand, b.brand, '#60a5fa', b.total]).filter(i => i[3])
              } />
            </>
          )}
          {precision.length > 0 && (
            <>
              <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1.8 }} />
              <Box>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.6,
                                  textTransform: 'uppercase', color: 'rgba(232,228,248,0.3)', mb: 1 }}>
                  Channel precision (of triaged)
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {precision.map(p => {
                    const pct = Math.round((p.precision ?? 0) * 100);
                    const color = pct >= 80 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171';
                    return (
                      <Box key={p.channel} display="flex" alignItems="center" gap={0.7}
                        sx={{ px: 1.1, py: 0.6, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.06)' }}>
                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(232,228,248,0.6)' }}>
                          {p.channel} <b style={{ color }}>{pct}%</b>
                          <span style={{ color: 'rgba(232,228,248,0.35)' }}> ({p.reviewed}/{p.triaged})</span>
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Panel>
      </Box>
    </Box>
  );
}

export default Overview;
